import React, { useEffect, useState } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { SectionHeader, Callout } from '../components/UI';
import { api, moneyFull, pct } from '../lib/api';

// Margin Analysis — advisor Spec 4 metrics, split out from the P&L page per
// Kat's feedback ("that just isn't a P&L as much as it is analysis lines").
//
// Classifications:
//   Variable → scales with revenue (Marketing acquisition + Sales commissions)
//   Fixed    → largely invariant to revenue (Fulfillment salaries + Overhead)
//   Owner    → owner-controlled comp / draws (Leadership)

const VARIABLE_BUCKETS = ['marketing', 'sales'];
const FIXED_BUCKETS = ['fulfillment', 'overhead'];
const OWNER_BUCKETS = ['leadership'];
const BUCKET_LABELS = {
  leadership: 'Leadership',
  marketing:  'Marketing',
  sales:      'Sales',
  fulfillment:'Fulfillment',
  overhead:   'Overhead',
};

export default function MarginAnalysis() {
  const { active, forecastData } = useScenario();
  const [actuals, setActuals] = useState([]);

  useEffect(() => {
    api.get('/actuals').then((r) => setActuals(r.data)).catch(() => {});
  }, []);

  const months = forecastData?.forecast?.months || [];

  const actualByKey = React.useMemo(() => {
    const m = {};
    (actuals || []).forEach((a) => {
      const key = a.month_label ? `${a.month_label}` : null;
      if (key) m[key] = a;
    });
    return m;
  }, [actuals]);

  // Build unified column set: actuals up through most recent actual, then forecast.
  const cols = React.useMemo(() => {
    const out = [];
    const actualKeys = new Set(actuals.map((a) => a.month_label));
    months.forEach((mo) => {
      if (actualKeys.has(mo.label)) {
        const a = actualByKey[mo.label];
        out.push({ label: mo.label, kind: 'actual', actual: a, forecast: mo });
      } else {
        out.push({ label: mo.label, kind: 'forecast', forecast: mo });
      }
    });
    return out;
  }, [months, actuals, actualByKey]);

  const revenueForCol = (col) =>
    col.kind === 'actual' ? col.actual.revenue : col.forecast.income.total;

  const bucketSetForCol = (col, keys) => keys.reduce((acc, k) => {
    const v = col.kind === 'actual'
      ? col.actual.buckets?.[k]
      : col.forecast.expenses[k]?.total;
    return v == null ? acc : acc + v;
  }, 0);

  const cmForCol = (col) => revenueForCol(col) - bucketSetForCol(col, VARIABLE_BUCKETS);
  const opIncomeForCol = (col) => cmForCol(col) - bucketSetForCol(col, FIXED_BUCKETS);
  const netIncomeForCol = (col) => opIncomeForCol(col) - bucketSetForCol(col, OWNER_BUCKETS);

  // Grand totals across the whole visible window.
  const totals = React.useMemo(() => {
    const rev = cols.reduce((a, c) => a + (revenueForCol(c) || 0), 0);
    const variable = cols.reduce((a, c) => a + bucketSetForCol(c, VARIABLE_BUCKETS), 0);
    const fixed = cols.reduce((a, c) => a + bucketSetForCol(c, FIXED_BUCKETS), 0);
    const owner = cols.reduce((a, c) => a + bucketSetForCol(c, OWNER_BUCKETS), 0);
    return {
      revenue: rev,
      variable,
      fixed,
      owner,
      cm: rev - variable,
      opIncome: rev - variable - fixed,
      netIncome: rev - variable - fixed - owner,
    };
  }, [cols]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!forecastData || !months.length) {
    return (
      <div data-testid="margin-analysis-page" className="space-y-8">
        <SectionHeader
          eyebrow="Analysis"
          title="Margin Analysis"
          description="Loading scenario forecast…"
        />
        <div className="label-editorial">Loading forecast data…</div>
      </div>
    );
  }

  const kpi = (label, value, sub) => (
    <div className="bg-white border-t-4 border-indie-navy p-5">
      <div className="label-editorial text-indie-ink/60">{label}</div>
      <div className="text-3xl font-semibold text-indie-navy num-tabular mt-1">{value}</div>
      {sub && <div className="text-[11px] text-indie-ink/60 mt-1">{sub}</div>}
    </div>
  );

  const cmPct = totals.revenue ? totals.cm / totals.revenue : null;
  const opIncomePct = totals.revenue ? totals.opIncome / totals.revenue : null;

  return (
    <div data-testid="margin-analysis-page" className="space-y-8">
      <SectionHeader
        eyebrow="Analysis"
        title="Margin Analysis"
        description={`Scenario "${active?.name || '—'}" · variable / fixed / owner breakdown, contribution margin, and operating income. Numbers are analysis lines — not a P&L.`}
      />

      <Callout
        tone="neutral"
        title="How to read this"
        body={
          <>
            <p><strong>Variable</strong> = Marketing acquisition + Sales commissions (scale with revenue).</p>
            <p><strong>Fixed</strong> = Fulfillment salaries + Overhead (rent, SaaS, insurance).</p>
            <p><strong>Owner-controlled</strong> = Leadership bucket (executive comp / owner draws).</p>
            <p className="mt-1"><strong>Contribution margin</strong> = Revenue − Variable. <strong>Operating income (pre-owner)</strong> = CM − Fixed. Both should trend up as the firm scales.</p>
          </>
        }
      />

      {/* Headline KPIs across the visible window */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {kpi('Revenue', moneyFull(totals.revenue), `${cols.length} months in view`)}
        {kpi('Contribution margin', moneyFull(totals.cm), cmPct != null ? `${pct(cmPct)} of revenue` : null)}
        {kpi('Operating income', moneyFull(totals.opIncome), opIncomePct != null ? `${pct(opIncomePct)} margin · pre-owner` : null)}
        {kpi('Net income', moneyFull(totals.netIncome), 'after owner-controlled comp')}
      </div>

      {/* Bucket band summary */}
      <div className="bg-white border-t-4 border-indie-navy p-6">
        <div className="label-editorial mb-4">Cost band totals</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indie-navy font-semibold mb-2">Variable · Marketing + Sales</div>
            <div className="text-2xl font-semibold text-indie-navy num-tabular">{moneyFull(totals.variable)}</div>
            <div className="text-[11px] text-indie-ink/60 mt-1">
              {totals.revenue ? `${pct(totals.variable / totals.revenue)} of revenue` : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indie-navy font-semibold mb-2">Fixed · Fulfillment + Overhead</div>
            <div className="text-2xl font-semibold text-indie-navy num-tabular">{moneyFull(totals.fixed)}</div>
            <div className="text-[11px] text-indie-ink/60 mt-1">
              {totals.revenue ? `${pct(totals.fixed / totals.revenue)} of revenue` : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-indie-navy font-semibold mb-2">Owner-controlled · Leadership</div>
            <div className="text-2xl font-semibold text-indie-navy num-tabular">{moneyFull(totals.owner)}</div>
            <div className="text-[11px] text-indie-ink/60 mt-1">
              {totals.revenue ? `${pct(totals.owner / totals.revenue)} of revenue` : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Month-by-month CM% and Op Income% */}
      <div className="bg-white border-t-4 border-indie-navy">
        <div className="p-5 border-b border-indie-navy/10">
          <div className="label-editorial">Monthly progression</div>
          <div className="text-sm text-indie-ink/60 mt-1">Contribution margin % and Operating income % across the visible window.</div>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="margin-analysis-table" className="w-full text-xs">
            <thead>
              <tr className="bg-indie-cream/40 text-left uppercase tracking-widest text-[10px] text-indie-navy">
                <th className="px-3 py-2 font-semibold">Row</th>
                {cols.map((c) => (
                  <th key={c.label} className="px-3 py-2 font-semibold text-right">
                    {c.label}
                    <span className={`ml-1 text-[9px] font-normal normal-case ${c.kind === 'actual' ? 'text-indie-evergreen' : 'text-indie-ink/50'}`}>
                      {c.kind === 'actual' ? 'actual' : 'forecast'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Revenue', fn: (c) => revenueForCol(c) },
                { label: 'Variable', fn: (c) => bucketSetForCol(c, VARIABLE_BUCKETS) },
                { label: 'Contribution margin', fn: (c) => cmForCol(c), bold: true },
                { label: 'CM %', fn: (c) => {
                  const rev = revenueForCol(c); const cm = cmForCol(c);
                  return rev ? pct(cm / rev) : '—';
                }, isPct: true },
                { label: 'Fixed', fn: (c) => bucketSetForCol(c, FIXED_BUCKETS) },
                { label: 'Operating income', fn: (c) => opIncomeForCol(c), bold: true },
                { label: 'Op. income %', fn: (c) => {
                  const rev = revenueForCol(c); const oi = opIncomeForCol(c);
                  return rev ? pct(oi / rev) : '—';
                }, isPct: true },
                { label: 'Owner-controlled', fn: (c) => bucketSetForCol(c, OWNER_BUCKETS) },
                { label: 'Net income', fn: (c) => netIncomeForCol(c), bold: true },
              ].map((row) => (
                <tr key={row.label} className={row.bold ? 'bg-indie-yellow/15 border-t border-indie-navy/10' : 'border-t border-indie-navy/5'}>
                  <td className={`px-3 py-2 ${row.bold ? 'font-semibold text-indie-navy' : 'text-indie-ink/80'} uppercase tracking-wider text-[11px]`}>{row.label}</td>
                  {cols.map((c) => {
                    const v = row.fn(c);
                    return (
                      <td key={`${row.label}-${c.label}`} className={`text-right px-3 py-2 num-tabular ${c.kind === 'actual' ? 'text-indie-evergreen' : 'text-indie-ink/70'} ${row.bold ? 'font-semibold' : ''}`}>
                        {v == null || v === '—' ? '—' : (row.isPct ? v : moneyFull(v))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 text-[11px] text-indie-ink/50 border-t border-indie-navy/10 leading-relaxed">
          These are analysis rows derived from the P&L bucket totals — not a formal P&L statement. See the <a href="/pnl" className="underline hover:text-indie-navy">P&L</a> page for the standard income statement view.
        </div>
      </div>
    </div>
  );
}
